import { CallResult, doApiGet, doApiPatch, doApiPost } from "./Requests";
import {
  AbsenceApprovalStatus,
  AbsenceEntry,
  CreateVacationQuotaRequest,
  UpdateAbsenceRequest,
  VacationQuota,
  UpdateVacationQuotaRequest,
  CurrentVacationQuota,
} from "../types/absence";
import { PaginationParameters, PaginationResult, toQueryParameters } from "./Pagination";

const buildQueryString = (params: Record<string, string | undefined>) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value && value.length > 0) {
      searchParams.append(key, value);
    }
  });
  const query = searchParams.toString();
  return query.length > 0 ? `?${query}` : "";
};

export const absenceApi = {
  getCompanyAbsencesPaginated: (
    serviceModuleId: number,
    params: PaginationParameters & {
      employeeUserId?: string;
      startDate?: string;
      endDate?: string;
    }
  ) => {
    const {
      offset,
      limit,
      search,
      sort,
      order,
      employeeUserId,
      startDate,
      endDate,
    } = params;
    const paginationQuery = toQueryParameters({
      offset,
      limit,
      search,
      sort,
      order,
    });
    const filterQuery = buildQueryString({
      employeeUserId: employeeUserId ?? undefined,
      startDate: startDate ?? undefined,
      endDate: endDate ?? undefined,
    });
    return doApiGet<PaginationResult<AbsenceEntry>>(
      `/api/companies/current/servicemodules/absence/${serviceModuleId}/absences/company/paginated` +
      `?${paginationQuery}${filterQuery.replace("?", "&")}`
    );
  },

  getCompanyAbsences: (
    serviceModuleId: number,
    params: {
      employeeUserId?: string;
      startDate?: string;
      endDate?: string;
    }
  ) => {
    const filterQuery = buildQueryString({
      employeeUserId: params.employeeUserId ?? undefined,
      startDate: params.startDate ?? undefined,
      endDate: params.endDate ?? undefined,
    });
    return doApiGet<AbsenceEntry[]>(
      `/api/companies/current/servicemodules/absence/${serviceModuleId}/absences/company` +
      `${filterQuery}`
    );
  },

  updateAbsence: (
    serviceModuleId: number,
    id: number,
    status: AbsenceApprovalStatus
  ): Promise<CallResult<AbsenceEntry>> => {
    const payload: UpdateAbsenceRequest = {
      absenceApproval: status,
    };

    return doApiPatch<UpdateAbsenceRequest, AbsenceEntry>(
      `/api/companies/current/servicemodules/absence/${serviceModuleId}/absences/company/${id}`,
      payload
    );
  },

  createVacationQuota: (
    serviceModuleId: number,
    payload: CreateVacationQuotaRequest
  ): Promise<CallResult<unknown>> => {
    return doApiPost<CreateVacationQuotaRequest, unknown>(
      `/api/companies/current/servicemodules/absence/${serviceModuleId}/vacationquota`,
      payload
    );
  },

  getVacationQuotas: (
    serviceModuleId: number,
    employeeId: string
  ): Promise<CallResult<VacationQuota[]>> => {
    return doApiGet<VacationQuota[]>(
      `/api/companies/current/servicemodules/absence/${serviceModuleId}/vacationquota?employeeId=${employeeId}`
    );
  },

  getCurrentVacationQuota: (
    serviceModuleId: number,
    employeeId: string
  ): Promise<CallResult<CurrentVacationQuota>> => {
    return doApiGet<CurrentVacationQuota>(
      `/api/companies/current/servicemodules/absence/${serviceModuleId}/vacationquota/current?employeeId=${employeeId}&loadSurplus=true`
    );
  },

  updateVacationQuota: (
    serviceModuleId: number,
    vacationQuotaId: number,
    payload: UpdateVacationQuotaRequest,
    options?: {
      loadAbsences?: boolean;
      loadSurplus?: boolean;
    }
  ): Promise<CallResult<VacationQuota>> => {
    const queryParams = new URLSearchParams({
      vacationQuotaId: String(vacationQuotaId),
    });
    if (options?.loadAbsences !== undefined) {
      queryParams.set("loadAbsences", String(options.loadAbsences));
    }
    if (options?.loadSurplus !== undefined) {
      queryParams.set("loadSurplus", String(options.loadSurplus));
    }
    const query = queryParams.toString();
    return doApiPatch<UpdateVacationQuotaRequest, VacationQuota>(
      `/api/companies/current/servicemodules/absence/${serviceModuleId}/vacationquota?${query}`,
      payload
    );
  },

  exportAbsences: (
      serviceModuleId: number,
      payload: {
        employeeIds: string[];
        startDate: string;
        endDate: string;
        fileFormat: "XLSX" | "CSV" | "PDF";
      }
  ): Promise<CallResult<Blob>> => {
    return doApiPost<any, Blob>(
        `/api/companies/current/servicemodules/absence/${serviceModuleId}/absences/reports`,
        payload,
        {
          raw: true,
          headers: {
            Accept: "application/octet-stream",
          },
        } as any
    );
  },

};
